import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import { isValidPassword } from '../../utils/form-utils';

export default function ResetPassword() {
  const { resetPassword, confirmResetPassword } = useAuthContext();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setError('');
    if (sent) {
      const password = String(data.get('password'));
      if (!isValidPassword(password)) {
        setError('The new password does not meet the password requirements.');
        return;
      }
      setBusy(true);
      confirmResetPassword(
        { username: email, newPassword: password, confirmationCode: String(data.get('code')) },
        {
          onSuccess: () => {
            setDone(true);
            setBusy(false);
          },
          onError: () => {
            setError('Unable to reset your password. Check the code or request a new one.');
            setBusy(false);
          },
        },
      );
    } else {
      const username = String(data.get('email')).trim().toLowerCase();
      setBusy(true);
      resetPassword(username, {
        onSuccess: () => {
          setEmail(username);
          setSent(true);
          setBusy(false);
        },
        onError: () => {
          setError(
            'Unable to request a reset. Check your email address or contact the team arranging your MacBook.',
          );
          setBusy(false);
        },
      });
    }
  }
  return (
    <>
      <h1 className="govuk-heading-l">{done ? 'Password reset' : 'Reset your password'}</h1>
      {error && (
        <p className="govuk-error-message" role="alert">
          {error}
        </p>
      )}
      {done ? (
        <Link className="govuk-link" to="/homebrew/signin">
          Sign in
        </Link>
      ) : (
        <form onSubmit={submit}>
          {!sent ? (
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="reset-email">
                Email address
              </label>
              <input
                className="govuk-input"
                id="reset-email"
                name="email"
                type="email"
                autoComplete="username"
                required
              />
            </div>
          ) : (
            <>
              <p className="govuk-body">Check your email for the reset code.</p>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="reset-code">
                  Reset code
                </label>
                <input
                  className="govuk-input"
                  id="reset-code"
                  name="code"
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="reset-password">
                  New password
                </label>
                <p className="govuk-hint" id="password-hint">
                  Use at least 8 characters, including uppercase and lowercase letters, a number and
                  a symbol.
                </p>
                <input
                  className="govuk-input"
                  id="reset-password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  aria-describedby="password-hint"
                  required
                />
              </div>
            </>
          )}
          <button className="govuk-button" type="submit" disabled={busy}>
            {sent ? 'Reset password' : 'Send reset code'}
          </button>
          {sent && (
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              disabled={busy}
              onClick={() => setSent(false)}
            >
              Request another code
            </button>
          )}
        </form>
      )}
    </>
  );
}
